import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export type ResolvedTheme = 'dark' | 'light';

type Theme = ReturnType<typeof useTheme>['theme'];

export function resolveTheme(theme: Theme, systemPrefersDark: boolean): ResolvedTheme {
  if (theme === 'light' || theme === 'soft-light') {
    return 'light';
  }
  if (theme === 'dark') {
    return 'dark';
  }
  return systemPrefersDark ? 'dark' : 'light';
}

/** Effective light/dark appearance from app theme + OS preference when theme is "system". */
export function useResolvedTheme(): ResolvedTheme {
  const { theme } = useTheme();
  const [systemPrefersDark, setSystemPrefersDark] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false,
  );

  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  return useMemo(
    () => resolveTheme(theme, systemPrefersDark),
    [theme, systemPrefersDark],
  );
}
